<script lang="ts">
	import type { OperationModel } from '@app/models/api/OperationModel';
	import Box from '$lib/components/boxes/Box.svelte';
	import { t, number } from 'svelte-i18n';
	import type { InputRangeProps } from '$lib/elements/inputs/types';
	import { getPerformance, isTaxExempted } from '@app/utils/operations';
	import { capitalize, formatAmountWithCurrency } from '$lib/utils/strings';
	import ProgressIndicator from '$lib/elements/ProgressIndicator.svelte';
	import type { PerformanceType } from '$lib/types/props';
	import { debounceAsync } from '@app/utils/global';
	import SliderIntention from './elements/intention/SliderIntention.svelte';
	import { OperationFinancialInstrumentEnum } from '@app/models/api/enums/OperationFinancialInstrumentEnum';
	import { page } from '$app/stores';
	import { OperationsTypeInteretsEnum } from '@app/models/api/enums/ObligationsTypeInteretsEnum';

	export let operation: OperationModel;

	let performance: PerformanceType = { value: 0 };
	let isFetchingPerformance = false;
	let sliderIntentionProps: InputRangeProps;

	sliderIntentionProps = {
		value: operation.minCoupon
	};

	const getPerformanceValue = async () => {
		let isDette = operation.financialInstrument == OperationFinancialInstrumentEnum.BONDS;
		let hasDefiscalisation = operation.isIRPPTaxExempted;

		if (isDette || hasDefiscalisation) {
			isFetchingPerformance = true;
			try {
				performance = await getPerformance(operation, sliderIntentionProps.value as number);
			} catch (e) {
				performance = { value: 0 };
			} finally {
				isFetchingPerformance = false;
			}
		}
	};

	const debouncePerformance = debounceAsync(async () => getPerformanceValue());

	$: if (
		sliderIntentionProps &&
		sliderIntentionProps.value &&
		operation.obligationsTypeRemboursementInterets !==
			(OperationsTypeInteretsEnum.INTERETS_TRIMESTRIELS_SUR_CAPITAL_AMORTI &&
				OperationsTypeInteretsEnum.INTERETS_MENSUELS_SUR_CAPITAL_AMORTI)
	) {
		debouncePerformance();
	}
</script>

{#if $page.data.user}
	<Box props={{}}>
		<div class="font-semibold font-display text-2xl">
			{capitalize($t('operation.performance.label'))}
		</div>
		<SliderIntention bind:sliderProps={sliderIntentionProps} {operation} />
		{#if performance.value}
			{#if isFetchingPerformance}
				<ProgressIndicator appearance="circular" color="white" size="small" />
			{:else}
				<div>
					{#if isTaxExempted(operation)}
						<p>
							-{@html $t('operation.performance.exemption', {
								values: { value: formatAmountWithCurrency(performance.value) }
							})}
						</p>
					{:else if performance.rateOfReturn && performance.nbMonths}
						<p>
							+{@html $t('operation.performance.details', {
								values: {
									value: formatAmountWithCurrency(performance.value),
									percentage: $number(performance.rateOfReturn, {
										style: 'percent',
										maximumSignificantDigits: 3
									}),
									nbMonths: performance.nbMonths
								}
							})}
						</p>
					{/if}
				</div>
			{/if}
		{/if}
	</Box>
{/if}
